import React, { useState } from 'react';
import PropTypes from 'prop-types';

const UserAvatar = ({ name, role, imageUrl, initials, avatarBg, size = 36, stacked = false, onClick, forceExpand = false, disableExpand = false, onRemove, removeLoading = false }) => {
    const [hovered, setHovered] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Thick border separation
    const borderSize = size >= 50 ? 3 : 2;
    const innerSize = size - borderSize * 2;

    const isAdmin = String(role || '').toLowerCase().includes('admin');
    const isManager = !isAdmin && !String(role || '').toLowerCase().includes('hr') && (
        String(role || '').toLowerCase().includes('manage') ||
        String(role || '').toLowerCase().includes('pmo') ||
        String(role || '').toLowerCase().includes('pm')
    );
    const crownSize = size >= 50 ? 20 : 16;
    
    const isExpanded = forceExpand || (hovered && !disableExpand);

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
            style={{
                position: 'relative',
                display: 'inline-flex',
                zIndex: hovered ? 99 : (stacked ? 1 : 'auto'),
                marginRight: (!disableExpand && hovered) ? 4 : (stacked ? -(size * 0.3) : 0),
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            {/* Premium Gold Crown Badge for Admin centered on top */}
            {isAdmin && (
                <div style={{
                    position: 'absolute',
                    top: -crownSize * 0.3,
                    left: size / 2,
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #fcd34d 0%, #d97706 100%)',
                    borderRadius: '50%',
                    width: crownSize,
                    height: crownSize,
                    border: '1.5px solid var(--ant-color-bg-container, #ffffff)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                    boxSizing: 'border-box',
                    pointerEvents: 'none'
                }}>
                    <svg viewBox="0 0 24 24" width="60%" height="60%" fill="#fff" style={{ display: 'block' }}>
                        <path d="M2 21h20v-2H2v2zM2 7l4 5 6-9 6 9 4-5v10H2V7z" />
                    </svg>
                </div>
            )}

            {/* Premium Briefcase Badge for PM / PMO centered on top */}
            {isManager && (
                <div style={{
                    position: 'absolute',
                    top: -crownSize * 0.3,
                    left: size / 2,
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                    borderRadius: '50%',
                    width: crownSize,
                    height: crownSize,
                    border: '1.5px solid var(--ant-color-bg-container, #ffffff)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                    boxSizing: 'border-box',
                    pointerEvents: 'none'
                }}>
                    <svg viewBox="0 0 24 24" width="55%" height="55%" fill="#fff" style={{ display: 'block' }}>
                        <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
                    </svg>
                </div>
            )}

            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    background: isExpanded ? 'var(--ant-color-fill-content, #f3f4f6)' : 'transparent',
                    border: isExpanded ? '1px solid var(--ant-color-border-secondary, #e5e7eb)' : '1px solid transparent',
                    borderRadius: 999,
                    padding: isExpanded ? `3px 16px 3px 3px` : '0px',
                    maxWidth: isExpanded ? 280 : size,
                    height: size,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth ease-in-out, no bounce
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    gap: 10,
                    whiteSpace: 'nowrap',
                    boxShadow: isExpanded ? '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)' : 'none',
                    // No scale transformation (flat expansion, no bouncing)
                }}
            >
                <div style={{
                    width: innerSize,
                    height: innerSize,
                    borderRadius: '50%',
                    background: (imageUrl && !imageError) ? 'transparent' : (avatarBg || '#10b981'),
                    color: (imageUrl && !imageError) ? 'inherit' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: size * 0.35,
                    flexShrink: 0,
                    overflow: 'hidden',
                    // Thick white border
                    border: `${borderSize}px solid var(--ant-color-bg-container, #ffffff)`,
                    // Crisp gray outline so that white avatars or images don't bleed or look blank
                    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.05)',
                    boxSizing: 'border-box'
                }}>
                    {(imageUrl && !imageError) ? (
                        <img
                            src={imageUrl}
                            alt={name}
                            onError={() => setImageError(true)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        initials || (name ? name.charAt(0) : 'U')
                    )}
                </div>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: isExpanded ? 1 : 0,
                    transform: isExpanded ? 'translateX(0)' : 'translateX(-8px)',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    transitionDelay: isExpanded ? '0.05s' : '0s',
                    pointerEvents: 'none',
                    lineHeight: 1.25,
                    justifyContent: 'center'
                }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ant-color-text, #111827)' }}>{name}</span>
                    {role && <span style={{ fontSize: 10, color: 'var(--ant-color-text-secondary, #6b7280)', fontWeight: 500 }}>{role}</span>}
                </div>
            </div>

            {/* Red remove dot — top-right of the circle, only when hovered and onRemove is provided */}
            {onRemove && hovered && (
                <div
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    style={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#ff4d4f',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                        border: '2px solid #fff',
                        zIndex: 200,
                        lineHeight: 1,
                    }}
                >
                    ✕
                </div>
            )}
        </div>
    );
};

UserAvatar.propTypes = {
    name: PropTypes.string.isRequired,
    role: PropTypes.string,
    imageUrl: PropTypes.string,
    initials: PropTypes.string,
    avatarBg: PropTypes.string,
    size: PropTypes.number,
    stacked: PropTypes.bool,
    onClick: PropTypes.func,
    forceExpand: PropTypes.bool,
    disableExpand: PropTypes.bool,
    onRemove: PropTypes.func,
    removeLoading: PropTypes.bool,
};

export default UserAvatar;
